import React from 'react';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import type { Toast, ToastVariant } from '../contexts/ToastContext';

const VARIANT_STYLES: Record<ToastVariant, string> = {
  success:
    'bg-qc-success/10 border-qc-success/30 text-qc-success',
  error:
    'bg-qc-error/10 border-qc-error/30 text-qc-error',
  warning:
    'bg-qc-warning/10 border-qc-warning/30 text-qc-warning',
  info: 'bg-interactive-primary/10 border-interactive-primary/30 text-interactive-primary',
};

const VARIANT_ICONS: Record<ToastVariant, React.ReactNode> = {
  success: <CheckCircle className="w-4 h-4 shrink-0" />,
  error: <AlertCircle className="w-4 h-4 shrink-0" />,
  warning: <AlertTriangle className="w-4 h-4 shrink-0" />,
  info: <Info className="w-4 h-4 shrink-0" />,
};

interface ToastItemProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

export function ToastItem({ toast, onRemove }: ToastItemProps) {
  return (
    <div
      role="alert"
      aria-live="polite"
      className={`flex items-start gap-3 w-80 p-4 rounded-lg border shadow-lg animate-in slide-in-from-right duration-300 ${VARIANT_STYLES[toast.variant]}`}
      data-testid={`toast-${toast.variant}`}
    >
      {VARIANT_ICONS[toast.variant]}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold">{toast.title}</p>
        {toast.description && (
          <p className="text-xs mt-0.5 opacity-80">{toast.description}</p>
        )}
      </div>
      <button
        onClick={() => onRemove(toast.id)}
        aria-label="Dismiss notification"
        className="shrink-0 opacity-60 hover:opacity-100 transition-opacity"
        data-testid="toast-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

interface ToastContainerProps {
  toasts: Toast[];
  onRemove: (id: string) => void;
}

export function ToastContainer({ toasts, onRemove }: ToastContainerProps) {
  if (toasts.length === 0) return null;
  return (
    <div
      className="fixed top-4 right-4 z-[9999] flex flex-col gap-2"
      aria-label="Notifications"
      aria-live="polite"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onRemove={onRemove} />
      ))}
    </div>
  );
}
